import { useCallback, useContext, useEffect, useState } from "preact/hooks";
import { ComponentChildren, createContext } from "preact";

import { ApiError } from "@api/base/api-caller";
import {
  addWhitelistExpiry,
  cancelWhitelistInvite,
  fetchActivatedUsers,
  fetchWhitelistUsers,
  forceLogOutUser,
  whitelistUser,
} from "@api/user-management";

import { usePopup } from "@contexts/popup";
import { ActivatedUser, WhitelistUser } from "@interfaces/user";
import { transformManagementEvent } from "@transformers/management";
import {
  transformActivatedUsers,
  transformWhitelistUsers,
} from "@transformers/users";

import { trackError } from "@utils/analytics";

interface Meta {
  ready: boolean;
  activatedUsers: ActivatedUser[];
  whitelistUsers: WhitelistUser[];

  whitelist: (
    username: string,
    phoneNumber: string,
    expiry?: number,
  ) => Promise<void>;

  addInviteExpiry: (
    username: string,
    phoneNumber: string,
    expiry: number,
  ) => Promise<void>;

  cancelInvite: (
    username: string,
    phoneNumber: string,
  ) => Promise<void>;

  forceLogOut: (username: string) => Promise<void>;
}

interface ProviderProps {
  children: ComponentChildren;
}

const DeliveryContext = createContext<Meta | null>(null);

export function UserManagementProvider({ children }: ProviderProps) {
  const { alert } = usePopup();
  const [ready, setReady] = useState(false);
  const [activatedUsers, setActivatedUsers] = useState<ActivatedUser[]>([]);
  const [whitelistUsers, setWhitelistUsers] = useState<WhitelistUser[]>([]);

  const whitelist = useCallback(async (
    username: string,
    phoneNumber: string,
    expiry: number = 259200,
  ) => {
    try {
      const data = transformManagementEvent(
        await whitelistUser(username, phoneNumber, expiry)
      );

      if (!data.success) {
        alert(
          data.reason || "Something went wrong!",
          "error",
        );
      } else {
        alert(
          data.reason || "Action successful!",
          "success",
          "Whitelisted",
        )
      }
    } catch (error: ApiError | any | unknown) {
      trackError(error);

      alert(
        (error.data as any)?.detail?.message || "Something went wrong!",
        "error",
      );
    }
  }, []);

  const addInviteExpiry = useCallback(async (
    username: string,
    phoneNumber: string,
    expiry: number,
  ) => {
    try {
      const data = transformManagementEvent(
        await addWhitelistExpiry(username, phoneNumber, expiry)
      );

      if (!data.success) {
        alert(
          data.reason || "Something went wrong!",
          "error",
        );
      } else {
        alert(
          data.reason || "Action successful!",
          "success",
          "Expiry Updated",
        )
      }
    } catch (error: ApiError | any | unknown) {
      trackError(error);

      alert(
        (error.data as any)?.detail?.message || "Something went wrong!",
        "error",
      );
    }
  }, []);

  const cancelInvite = useCallback(async (
    username: string,
    phoneNumber: string,
  ) => {
    try {
      const data = transformManagementEvent(
        await cancelWhitelistInvite(username, phoneNumber)
      );

      if (!data.success) {
        alert(
          data.reason || "Something went wrong!",
          "error",
        );
      } else {
        alert(
          data.reason || "Action successful!",
          "success",
          "Invite Cancelled",
        )
      }
    } catch (error: ApiError | any | unknown) {
      trackError(error);

      alert(
        (error.data as any)?.detail?.message || "Something went wrong!",
        "error",
      );
    }
  }, []);

  const forceLogOut = useCallback(async (username: string) => {
    try {
      const { success, reason } = transformManagementEvent(
        await forceLogOutUser(username)
      );

      if (!success) {
        alert(
          reason || "Something went wrong!",
          "error",
        );
      } else {
        alert(
          reason || "Action successful!",
          "success",
          "Logged Out",
        )
      }
    } catch (error: ApiError | any | unknown) {
      trackError(error);

      alert(
        (error.data as any)?.detail?.message || "Something went wrong!",
        "error",
      );
    }
  }, []);

  const load = useCallback(async () => {
    setReady(false);

    try {
      const activatedUsers = transformActivatedUsers(
        await fetchActivatedUsers()
      );

      setActivatedUsers(activatedUsers);
    } catch (error: ApiError | any | unknown) {
      trackError(error);

      alert(
        (error.data as any)?.detail?.message || "Something went wrong!",
        "error",
      );
    }

    try {
      const whitelistUsers = transformWhitelistUsers(
        await fetchWhitelistUsers()
      );

      setWhitelistUsers(whitelistUsers);
    } catch (error: ApiError | any | unknown) {
      trackError(error);

      alert(
        (error.data as any)?.detail?.message ||

        "Can't load whitelist users!",
        "error",
      );
    }

    setReady(true);
  }, []);

  useEffect(() => {
    load();
  }, []);

  const value = {
    ready,
    activatedUsers,
    whitelistUsers,
    whitelist,
    cancelInvite,
    addInviteExpiry,
    forceLogOut,
  };

  return <DeliveryContext.Provider value={value}>
    {children}
  </DeliveryContext.Provider>
}

export function useUserManagement(): Meta {
  return useContext(DeliveryContext)!;
}

